import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "./api";
import type {
  AdminBlock,
  AdminLesson,
  AdminSection,
  Block,
  Lesson,
  Me,
  Section,
} from "./types";
import Home from "./screens/Home";
import ParasitesSelect from "./screens/ParasitesSelect";
import MyParasites from "./screens/MyParasites";
import LessonScreen from "./screens/Lesson";
import SectionStub from "./screens/SectionStub";
import SectionLessons from "./screens/SectionLessons";
import DailyHomework from "./screens/DailyHomework";
import Settings from "./screens/Settings";
import {
  AdminBlockEdit,
  AdminBlocks,
  AdminLessonEdit,
  AdminLessons,
  AdminSectionEdit,
  AdminSections,
} from "./screens/Admin";
import AdminUITest from "./screens/AdminUITest";
import { useBackButton } from "./tg";

type Route =
  | { name: "home" }
  // from: куда вернуться по BackButton. Home при первом входе, my-parasites при "Изменить выбор".
  | { name: "parasites-select"; from: "home" | "my-parasites" }
  | { name: "my-parasites" }
  // Generic-список уроков для любой секции кроме parasites.
  | { name: "section-lessons"; section: Section }
  // Экран сегодняшнего ДЗ — не список уроков, а подборка упражнений.
  | { name: "daily-homework" }
  // from определяет куда возвращаться: для parasites — в my-parasites, для секции —
  // в section-lessons. Из ДЗ в Lesson не ходим — упражнения интерактивны прямо на экране ДЗ.
  | {
      name: "lesson";
      lesson: Lesson;
      from:
        | { kind: "parasites" }
        | { kind: "section"; section: Section };
    }
  // Заглушка для секций без специальной обработки (на всякий случай).
  | { name: "section-stub"; title: string }
  // Экран настроек (пока заглушка, контент появится позже).
  | { name: "settings" }
  // --- Admin -------------------------------------------------------------
  | { name: "admin-sections" }
  | { name: "admin-section-edit"; initial: AdminSection | null; existingCount: number }
  | { name: "admin-lessons"; section: AdminSection }
  | {
      name: "admin-lesson-edit";
      section: AdminSection;
      initial: AdminLesson | null;
      existingCount: number;
    }
  | { name: "admin-blocks"; lesson: AdminLesson; section: AdminSection }
  | {
      name: "admin-block-edit";
      lesson: AdminLesson;
      section: AdminSection;
      initial: AdminBlock | null;
      existingCount: number;
    }
  // Песочница для сравнения вариантов рендера блока урока.
  | { name: "admin-ui-test" };

type Boot =
  | { kind: "loading" }
  | { kind: "ready"; me: Me }
  | { kind: "no-tg" }
  | { kind: "error"; message: string };

// Кеш данных живёт в App, чтобы при возврате назад (через свайп/BackButton) экраны
// рендерили контент мгновенно из памяти, а не показывали спиннер заново.
// undefined = ни разу не загружали, [] = загружали и пусто. Это важная разница для UI.
type DataCache = {
  sections: Section[] | undefined;
  // Отдельный кеш для админки: AdminSection это другой view (без прогресса юзера,
  // плюс админ видит и скрытые секции). Использовать общий cache.sections нельзя.
  adminSections: AdminSection[] | undefined;
  parasitesLessons: Lesson[] | undefined;
  // Уроки прочих секций по slug. parasites сюда не кладём — у них свой кеш выше.
  lessonsBySection: Record<string, Lesson[]>;
  blocksByLesson: Record<string, Block[]>;
};

// Локальный state экранов, который должен переживать unmount при свайпе назад
// (например, чтобы возврат к ParasitesSelect показал те же чекбоксы).
type ScreenState = {
  pendingParasitesSelection: Set<string> | null;
  lessonState: Record<string, { currentBlockIndex: number }>;
  // Активный блок в Дневных — `block_id`. По индексу хранить нельзя: между
  // визитами список может перетасоваться (untake, новые required после
  // прохождения урока), индекс перестанет совпадать.
  dailyHomeworkPosition: string | null;
};

export default function App() {
  const tg = window.Telegram?.WebApp;
  const initData = tg?.initData ?? "";

  const [boot, setBoot] = useState<Boot>({ kind: "loading" });
  const [route, setRoute] = useState<Route>({ name: "home" });

  const [cache, setCache] = useState<DataCache>({
    sections: undefined,
    adminSections: undefined,
    parasitesLessons: undefined,
    lessonsBySection: {},
    blocksByLesson: {},
  });
  const [screenState, setScreenState] = useState<ScreenState>({
    pendingParasitesSelection: null,
    lessonState: {},
    dailyHomeworkPosition: null,
  });

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
    }
  }, [tg]);

  useEffect(() => {
    if (!initData) {
      setBoot({ kind: "no-tg" });
      return;
    }
    api<Me>("/me")
      .then((me) => setBoot({ kind: "ready", me }))
      .catch((e: Error) => setBoot({ kind: "error", message: e.message }));
  }, [initData]);

  // Sections нужны не только на Home (для меток/прогресса), но и на дочерних
  // экранах — например, заголовок «Детокс речи» на MyParasites/ParasitesSelect.
  // Раньше грузились только в Home; deep-route напрямую в parasites показывал
  // fallback "Мои паразиты". Грузим на App-уровне один раз после авторизации.
  useEffect(() => {
    if (boot.kind !== "ready") return;
    if (cache.sections !== undefined) return;
    api<Section[]>("/sections")
      .then((sections) => setCache((c) => ({ ...c, sections })))
      .catch(() => {
        // Тихо: Home сам подгрузит при первом открытии, fallback'и на местах.
      });
  }, [boot.kind, cache.sections]);

  // --- Cache mutators (стабильные ссылки через useCallback) -----------------

  const setSections = useCallback((sections: Section[]) => {
    setCache((c) => ({ ...c, sections }));
  }, []);

  const setAdminSections = useCallback((adminSections: AdminSection[]) => {
    setCache((c) => ({ ...c, adminSections }));
  }, []);

  const setParasitesLessons = useCallback((lessons: Lesson[]) => {
    setCache((c) => ({ ...c, parasitesLessons: lessons }));
  }, []);

  const setSectionLessons = useCallback((slug: string, lessons: Lesson[]) => {
    setCache((c) => ({
      ...c,
      lessonsBySection: { ...c.lessonsBySection, [slug]: lessons },
    }));
  }, []);

  const setLessonBlocks = useCallback((lessonId: string, blocks: Block[]) => {
    setCache((c) => ({
      ...c,
      blocksByLesson: { ...c.blocksByLesson, [lessonId]: blocks },
    }));
  }, []);

  // После complete блока — обновляем cached blocks и cached lessons (счётчик прогресса).
  // Если урок завершён — также инвалидируем sections (прогресс секции меняется).
  const onBlockCompleted = useCallback(
    (lessonId: string, blockId: string, lessonCompleted: boolean) => {
      setCache((c) => {
        const cachedBlocks = c.blocksByLesson[lessonId];
        const nextBlocksByLesson = cachedBlocks
          ? {
              ...c.blocksByLesson,
              [lessonId]: cachedBlocks.map((b) =>
                b.id === blockId ? { ...b, completed: true } : b,
              ),
            }
          : c.blocksByLesson;

        const bumpLesson = (l: Lesson) => {
          if (l.id !== lessonId) return l;
          const blocks_completed = Math.min(l.blocks_total, l.blocks_completed + 1);
          return {
            ...l,
            blocks_completed,
            completed: lessonCompleted || l.completed,
          };
        };

        const nextParasitesLessons = c.parasitesLessons?.map(bumpLesson);

        // Обновляем все секции-кеши, где есть этот урок. Урок принадлежит ровно одной
        // секции, но искать её по lessonId дороже — проходим map'ом, ленивый no-op для не-совпадений.
        const nextLessonsBySection: Record<string, Lesson[]> = {};
        let lessonsBySectionChanged = false;
        for (const [slug, lessons] of Object.entries(c.lessonsBySection)) {
          const updated = lessons.map(bumpLesson);
          nextLessonsBySection[slug] = updated;
          if (updated !== lessons) lessonsBySectionChanged = true;
        }

        return {
          ...c,
          blocksByLesson: nextBlocksByLesson,
          parasitesLessons: nextParasitesLessons,
          lessonsBySection: lessonsBySectionChanged
            ? nextLessonsBySection
            : c.lessonsBySection,
          // sections придётся перечитать в фоне при следующем возврате на Home.
          sections: lessonCompleted ? undefined : c.sections,
        };
      });
    },
    [],
  );

  const setPendingParasitesSelection = useCallback((sel: Set<string>) => {
    setScreenState((s) => ({ ...s, pendingParasitesSelection: sel }));
  }, []);

  const setLessonCurrentBlockIndex = useCallback((lessonId: string, idx: number) => {
    setScreenState((s) => ({
      ...s,
      lessonState: { ...s.lessonState, [lessonId]: { currentBlockIndex: idx } },
    }));
  }, []);

  const setDailyHomeworkPosition = useCallback((key: string | null) => {
    setScreenState((s) => ({ ...s, dailyHomeworkPosition: key }));
  }, []);

  // --- Routing --------------------------------------------------------------

  // Guard перехода: дочерний экран может зарегистрировать функцию, возвращающую
  // Promise<boolean> — true если можно уходить, false если юзер передумал. Если
  // guard не зарегистрирован — переход свободный. Сейчас используется
  // AdminBlockEdit для защиты от потери несохранённых изменений.
  const unsavedGuardRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerUnsavedGuard = useCallback(
    (g: (() => Promise<boolean>) | null) => {
      unsavedGuardRef.current = g;
    },
    [],
  );
  const navigateWithGuard = useCallback(async (nav: () => void) => {
    const g = unsavedGuardRef.current;
    if (g) {
      const ok = await g();
      if (!ok) return;
      unsavedGuardRef.current = null; // одноразово: после согласия — отпускаем
    }
    nav();
  }, []);

  // Централизованное управление Telegram BackButton — один useBackButton на всё приложение.
  // useMemo даёт стабильную ссылку до фактической смены route, чтобы useBackButton не
  // переподписывался на каждом рендере (на macOS Telegram это вызывало моргание / нереакцию).
  const backHandler = useMemo<(() => void) | null>(() => {
    const go = (nav: () => void) => () => {
      void navigateWithGuard(nav);
    };
    switch (route.name) {
      case "home":
        return null;
      case "parasites-select":
        return go(() =>
          setRoute(
            route.from === "my-parasites"
              ? { name: "my-parasites" }
              : { name: "home" },
          ),
        );
      case "my-parasites":
        return go(() => setRoute({ name: "home" }));
      case "lesson":
        return go(() => {
          if (route.from.kind === "parasites") {
            setRoute({ name: "my-parasites" });
          } else {
            setRoute({ name: "section-lessons", section: route.from.section });
          }
        });
      case "section-lessons":
        return go(() => setRoute({ name: "home" }));
      case "daily-homework":
        return go(() => setRoute({ name: "home" }));
      case "section-stub":
        return go(() => setRoute({ name: "home" }));
      case "settings":
        return go(() => setRoute({ name: "home" }));
      case "admin-sections":
        return go(() => setRoute({ name: "home" }));
      case "admin-section-edit":
        return go(() => setRoute({ name: "admin-sections" }));
      case "admin-lessons":
        return go(() => setRoute({ name: "admin-sections" }));
      case "admin-lesson-edit":
        return go(() =>
          setRoute({ name: "admin-lessons", section: route.section }),
        );
      case "admin-blocks":
        return go(() =>
          setRoute({ name: "admin-lessons", section: route.section }),
        );
      case "admin-block-edit":
        return go(() =>
          setRoute({
            name: "admin-blocks",
            lesson: route.lesson,
            section: route.section,
          }),
        );
      case "admin-ui-test":
        return go(() => setRoute({ name: "admin-sections" }));
    }
  }, [route, navigateWithGuard]);
  useBackButton(backHandler);

  if (boot.kind === "loading") {
    return (
      <div className="screen">
        <div className="card">Загрузка…</div>
      </div>
    );
  }

  if (boot.kind === "no-tg") {
    return (
      <div className="screen">
        <header>
          <h1>Демосфен</h1>
        </header>
        <div className="card">
          Откройте через{" "}
          <a href="https://t.me/ipo_lk_demosfen_bot" className="link">
            @ipo_lk_demosfen_bot
          </a>
          .
        </div>
      </div>
    );
  }

  if (boot.kind === "error") {
    return (
      <div className="screen">
        <header>
          <h1>Демосфен</h1>
        </header>
        <div className="error-card">Ошибка: {boot.message}</div>
      </div>
    );
  }

  const me = boot.me;

  // Маршрутизация — простой switch по state. Данные приходят из cache в App,
  // экраны рендерятся сразу если кеш есть, иначе сами вызывают set*-колбэки после fetch.
  switch (route.name) {
    case "home":
      return (
        <Home
          firstName={me.first_name}
          isAdmin={me.is_admin}
          sections={cache.sections}
          onSectionsLoaded={setSections}
          onOpenAdmin={() => setRoute({ name: "admin-sections" })}
          onOpenSettings={() => setRoute({ name: "settings" })}
          onOpenSection={(section) =>
            // Ежедневное ДЗ — это не список уроков, а подборка упражнений.
            // Маршрутизируем отдельно, остальные секции идут в generic SectionLessons.
            section.id === "daily_homework"
              ? setRoute({ name: "daily-homework" })
              : setRoute({ name: "section-lessons", section })
          }
          onContinueSection={async (section) => {
            // «Продолжить» с Home — открыть текущий урок секции сразу, минуя список.
            // У Section в API нет current_lesson_id, поэтому подтягиваем список уроков
            // (или берём из кеша) и находим первый незавершённый по display_order.
            // Если все пройдены — открываем последний урок. Fallback на список —
            // если запрос упал или уроков нет.
            try {
              const lessons =
                cache.lessonsBySection[section.id] ??
                (await api<Lesson[]>(`/sections/${section.id}/lessons`));
              if (!cache.lessonsBySection[section.id]) {
                setSectionLessons(section.id, lessons);
              }
              if (lessons.length === 0) {
                setRoute({ name: "section-lessons", section });
                return;
              }
              const sorted = [...lessons].sort(
                (a, b) => a.display_order - b.display_order,
              );
              const current =
                sorted.find(
                  (l) =>
                    !l.completed ||
                    (l.blocks_total > 0 && l.blocks_completed < l.blocks_total),
                ) ?? sorted[sorted.length - 1];
              setRoute({
                name: "lesson",
                lesson: current,
                from: { kind: "section", section },
              });
            } catch {
              setRoute({ name: "section-lessons", section });
            }
          }}
          onOpenParasites={async () => {
            // Один лишний запрос, но логика прозрачнее: на основе chosen решаем куда идти.
            // Если уроки уже в кеше — используем их, иначе запрашиваем.
            try {
              const lessons =
                cache.parasitesLessons ??
                (await api<Lesson[]>("/sections/parasites/lessons"));
              if (!cache.parasitesLessons) setParasitesLessons(lessons);
              const hasChosen = lessons.some((l) => l.chosen);
              setRoute(
                hasChosen
                  ? { name: "my-parasites" }
                  : { name: "parasites-select", from: "home" },
              );
            } catch {
              setRoute({ name: "parasites-select", from: "home" });
            }
          }}
        />
      );

    case "parasites-select":
      return (
        <ParasitesSelect
          initialLessons={cache.parasitesLessons}
          sectionTitle={cache.sections?.find((s) => s.id === "parasites")?.title}
          onLessonsLoaded={setParasitesLessons}
          pendingSelection={screenState.pendingParasitesSelection}
          onPendingSelectionChange={setPendingParasitesSelection}
          onSaved={(lessons) => {
            setParasitesLessons(lessons);
            // sections мог измениться (lessons_total/completed) — инвалидируем для свежести.
            setCache((c) => ({ ...c, sections: undefined }));
            setRoute({ name: "my-parasites" });
          }}
        />
      );

    case "my-parasites":
      return (
        <MyParasites
          initialLessons={cache.parasitesLessons}
          sectionTitle={cache.sections?.find((s) => s.id === "parasites")?.title}
          onLessonsLoaded={setParasitesLessons}
          onOpenLesson={(lesson) =>
            setRoute({ name: "lesson", lesson, from: { kind: "parasites" } })
          }
          onEditSelection={() =>
            setRoute({ name: "parasites-select", from: "my-parasites" })
          }
          onNoSelection={() =>
            setRoute({ name: "parasites-select", from: "home" })
          }
        />
      );

    case "section-lessons":
      return (
        <SectionLessons
          sectionSlug={route.section.id}
          sectionTitle={route.section.title}
          initialLessons={cache.lessonsBySection[route.section.id]}
          onLessonsLoaded={setSectionLessons}
          onOpenLesson={(lesson) =>
            setRoute({
              name: "lesson",
              lesson,
              from: { kind: "section", section: route.section },
            })
          }
        />
      );

    case "section-stub":
      return (
        <SectionStub
          title={route.title}
          onBack={() => setRoute({ name: "home" })}
        />
      );

    case "settings":
      return <Settings onBack={() => setRoute({ name: "home" })} />;

    case "daily-homework":
      return (
        <DailyHomework
          initialPositionKey={screenState.dailyHomeworkPosition}
          onPositionChange={setDailyHomeworkPosition}
          onBack={() => setRoute({ name: "home" })}
        />
      );

    case "lesson":
      return (
        <LessonScreen
          lessonId={route.lesson.id}
          lessonTitle={route.lesson.title}
          blocks={cache.blocksByLesson[route.lesson.id]}
          onBlocksLoaded={(blocks) => setLessonBlocks(route.lesson.id, blocks)}
          currentBlockIndex={
            screenState.lessonState[route.lesson.id]?.currentBlockIndex
          }
          onCurrentBlockIndexChange={(idx) =>
            setLessonCurrentBlockIndex(route.lesson.id, idx)
          }
          onBlockCompleted={(blockId, lessonCompleted) =>
            onBlockCompleted(route.lesson.id, blockId, lessonCompleted)
          }
          onLessonCompleted={() => {
            if (route.from.kind === "parasites") {
              setRoute({ name: "my-parasites" });
            } else {
              setRoute({ name: "section-lessons", section: route.from.section });
            }
          }}
        />
      );

    // --- Admin -------------------------------------------------------------

    case "admin-sections":
      return (
        <AdminSections
          initialSections={cache.adminSections}
          onSectionsLoaded={setAdminSections}
          onOpenSection={(section) =>
            setRoute({ name: "admin-lessons", section })
          }
          onEditSection={(initial, existingCount) =>
            setRoute({ name: "admin-section-edit", initial, existingCount })
          }
          onOpenUITest={() => setRoute({ name: "admin-ui-test" })}
        />
      );

    case "admin-section-edit":
      return (
        <AdminSectionEdit
          initial={route.initial}
          existingCount={route.existingCount}
          onSaved={() => {
            // Изменения могут касаться available/title — инвалидируем оба кеша
            // (пользовательский Home и админский список секций).
            setCache((c) => ({ ...c, sections: undefined, adminSections: undefined }));
            setRoute({ name: "admin-sections" });
          }}
        />
      );

    case "admin-lessons":
      return (
        <AdminLessons
          section={route.section}
          onOpenLesson={(lesson) =>
            setRoute({ name: "admin-blocks", lesson, section: route.section })
          }
          onEditLesson={(initial, existingCount) =>
            setRoute({
              name: "admin-lesson-edit",
              section: route.section,
              initial,
              existingCount,
            })
          }
        />
      );

    case "admin-lesson-edit":
      return (
        <AdminLessonEdit
          section={route.section}
          initial={route.initial}
          existingCount={route.existingCount}
          onSaved={() => {
            // Если юзер редактировал свои паразиты-уроки — инвалидируем кеш.
            setCache((c) => ({ ...c, parasitesLessons: undefined }));
            setRoute({ name: "admin-lessons", section: route.section });
          }}
        />
      );

    case "admin-blocks":
      return (
        <AdminBlocks
          lesson={route.lesson}
          onEditBlock={(block, existingCount) =>
            setRoute({
              name: "admin-block-edit",
              section: route.section,
              lesson: route.lesson,
              initial: block,
              existingCount,
            })
          }
        />
      );

    case "admin-block-edit":
      return (
        <AdminBlockEdit
          lesson={route.lesson}
          initial={route.initial}
          existingCount={route.existingCount}
          registerUnsavedGuard={registerUnsavedGuard}
          onAutosaved={() => {
            // Autosave обновил блок на бэке — пользовательский кеш блоков
            // (юзер мог открывать этот урок раньше) больше не актуален.
            // Инвалидируем сразу, чтобы при возврате на Lesson увидели свежее.
            setCache((c) => {
              if (!c.blocksByLesson[route.lesson.id]) return c;
              const next = { ...c.blocksByLesson };
              delete next[route.lesson.id];
              return { ...c, blocksByLesson: next };
            });
          }}
          onSaved={() => {
            // Если юзер уже открывал этот урок — кеш блоков устарел.
            setCache((c) => {
              const next = { ...c.blocksByLesson };
              delete next[route.lesson.id];
              return { ...c, blocksByLesson: next };
            });
            setRoute({
              name: "admin-blocks",
              lesson: route.lesson,
              section: route.section,
            });
          }}
        />
      );

    case "admin-ui-test":
      return <AdminUITest onBack={() => setRoute({ name: "admin-sections" })} />;
  }
}
